/**
 * IONLOOP Assistant — enterprise UI shell (Phase 1).
 *
 * Builds the floating trigger and the large two-column assistant panel with
 * knowledge navigation, conversation area and input. The shell is pure DOM
 * creation (textContent only — user/knowledge content is never rendered via
 * innerHTML) and wires keyboard/accessibility behavior.
 *
 * The shell is dumb: it delegates question handling and category selection to
 * callbacks provided by the orchestrator (assistant.js).
 */

var renderer = require('./render-answer');
var categoriesMod = require('./knowledge/categories');
var commandRouter = require('./command-router');

var CATEGORIES = categoriesMod.CATEGORIES;

var SUGGESTED_QUESTIONS = [
    'What is IONLOOP ID?',
    'What can I do with IONLOOP?',
    'How do trading tools work?',
    'What planning tools are available?'
];

function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) {
        node.className = className;
    }
    if (text !== undefined && text !== null) {
        node.textContent = text;
    }
    return node;
}

function makeIcon(kind) {
    var icon = el('i', 'ili-assistant-icon ri-' + kind);
    icon.setAttribute('aria-hidden', 'true');
    return icon;
}

/**
 * Build the assistant DOM and return its controller API.
 *
 * @param {Object} hooks
 *   onSubmit(question)
 *   onCategorySelect(categoryKey|null)
 *   getCategoryCounts() -> { category: count }
 */
function buildAssistantUI(hooks) {
    var state = {
        isOpen: false,
        isMinimized: false,
        isProcessing: false,
        selectedCategory: null,
        suggestionsRendered: false
    };

    // ------------------------------------------------------------------
    // Floating trigger
    // ------------------------------------------------------------------
    var trigger = el('button', 'ili-assistant-trigger');
    trigger.type = 'button';
    trigger.setAttribute('aria-label', 'Open IONLOOP Assistant');
    trigger.appendChild(makeIcon('chat-3-line'));

    // ------------------------------------------------------------------
    // Panel shell
    // ------------------------------------------------------------------
    var panel = el('div', 'ili-assistant-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-label', 'IONLOOP Assistant');
    panel.setAttribute('aria-hidden', 'true');

    // -- Header ----------------------------------------------------------
    var header = el('div', 'ili-assistant__header');
    var brand = el('div', 'ili-assistant__brand');
    var brandMark = el('span', 'ili-assistant__brand-mark', 'I');
    brandMark.setAttribute('aria-hidden', 'true');
    var brandText = el('div', 'ili-assistant__brand-text');
    var brandTitle = el('span', 'ili-assistant__brand-title', 'IONLOOP Assistant');
    var brandSub = el('span', 'ili-assistant__brand-sub', 'IONLOOP knowledge assistant');
    brandText.appendChild(brandTitle);
    brandText.appendChild(brandSub);
    brand.appendChild(brandMark);
    brand.appendChild(brandText);
    header.appendChild(brand);

    var headerActions = el('div', 'ili-assistant__header-actions');
    var minimizeBtn = el('button', 'ili-assistant__btn', '');
    minimizeBtn.type = 'button';
    minimizeBtn.setAttribute('aria-label', 'Minimize IONLOOP Assistant');
    minimizeBtn.appendChild(makeIcon('substract-line'));
    var closeBtn = el('button', 'ili-assistant__btn ili-assistant__btn--close', '');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close IONLOOP Assistant');
    closeBtn.appendChild(makeIcon('close-line'));
    headerActions.appendChild(minimizeBtn);
    headerActions.appendChild(closeBtn);
    header.appendChild(headerActions);

    // -- Body ------------------------------------------------------------
    var body = el('div', 'ili-assistant__body');

    // Left: knowledge navigation
    var nav = el('aside', 'ili-assistant__nav');
    nav.setAttribute('aria-label', 'Knowledge navigation');
    var navTitle = el('div', 'ili-assistant__nav-title', 'Knowledge');
    var navStatus = el('div', 'ili-assistant__nav-status', 'IONLOOP content');
    nav.appendChild(navTitle);
    nav.appendChild(navStatus);

    var navList = el('ul', 'ili-assistant__nav-list');
    CATEGORIES.forEach(function (cat) {
        var item = el('li', 'ili-assistant__nav-item');
        var btn = el('button', 'ili-assistant__nav-btn', '');
        btn.type = 'button';
        btn.setAttribute('data-category', cat.key);
        btn.setAttribute('aria-label', cat.label);
        var label = el('span', 'ili-assistant__nav-label', cat.label);
        var count = el('span', 'ili-assistant__nav-count', '');
        btn.appendChild(label);
        btn.appendChild(count);
        item.appendChild(btn);
        navList.appendChild(item);
    });
    nav.appendChild(navList);

    var navInfo = el('div', 'ili-assistant__nav-info');
    var infoTitle = el('p', 'ili-assistant__nav-info-title', 'IONLOOP Assistant');
    var infoText = el('p', 'ili-assistant__nav-info-text',
        'Answers are generated from IONLOOP\u2019s own product content.');
    var infoNoAi = el('p', 'ili-assistant__nav-info-ai', 'No external AI provider');
    navInfo.appendChild(infoTitle);
    navInfo.appendChild(infoText);
    navInfo.appendChild(infoNoAi);
    nav.appendChild(navInfo);

    // Mobile: knowledge drawer toggle
    var drawerToggle = el('button', 'ili-assistant__drawer-toggle', '');
    drawerToggle.type = 'button';
    drawerToggle.setAttribute('aria-label', 'Toggle knowledge categories');
    var drawerToggleText = el('span', '', 'Knowledge');
    drawerToggle.appendChild(drawerToggleText);
    drawerToggle.appendChild(makeIcon('menu-2-line'));

    // Right: conversation
    var main = el('div', 'ili-assistant__main');

    // Welcome state
    var welcome = el('div', 'ili-assistant__welcome');
    var welcomeIcon = el('div', 'ili-assistant__welcome-icon');
    welcomeIcon.appendChild(makeIcon('discuss-line'));
    var welcomeHeading = el('h2', 'ili-assistant__welcome-heading', 'How can I help?');
    var welcomeText = el('p', 'ili-assistant__welcome-text',
        'Ask about IONLOOP ID, trading tools, planning, account features, automation or the platform.');
    welcome.appendChild(welcomeIcon);
    welcome.appendChild(welcomeHeading);
    welcome.appendChild(welcomeText);

    var suggested = el('div', 'ili-assistant__suggested');
    SUGGESTED_QUESTIONS.forEach(function (q) {
        var btn = el('button', 'ili-assistant__suggested-btn', q);
        btn.type = 'button';
        suggested.appendChild(btn);
    });
    welcome.appendChild(suggested);

    // Message scroll area
    var messages = el('div', 'ili-assistant__messages');
    messages.setAttribute('aria-live', 'polite');
    messages.setAttribute('role', 'log');
    messages.appendChild(welcome);

    // Input area
    var inputArea = el('div', 'ili-assistant__input');
    var textarea = el('textarea', 'ili-assistant__textarea', '');
    textarea.setAttribute('placeholder', 'Ask about IONLOOP... Type / for commands');
    textarea.setAttribute('rows', '1');
    textarea.setAttribute('aria-label', 'Ask about IONLOOP');
    var sendBtn = el('button', 'ili-assistant__send', '');
    sendBtn.type = 'button';
    sendBtn.setAttribute('aria-label', 'Send message');
    sendBtn.disabled = true;
    sendBtn.appendChild(makeIcon('send-plane-fill'));
    inputArea.appendChild(textarea);
    inputArea.appendChild(sendBtn);

    // Command palette (autocomplete) — shown above the input when typing "/"
    var commandMenu = el('div', 'ili-assistant__command-menu');
    commandMenu.setAttribute('role', 'listbox');
    commandMenu.setAttribute('aria-label', 'Commands');
    commandMenu.setAttribute('aria-hidden', 'true');

    main.appendChild(messages);
    main.appendChild(commandMenu);
    main.appendChild(inputArea);

    body.appendChild(nav);
    body.appendChild(main);
    panel.appendChild(header);
    panel.appendChild(body);

    // ------------------------------------------------------------------
    // Category counts
    // ------------------------------------------------------------------
    function updateCategoryCounts(counts) {
        counts = counts || {};
        var buttons = navList.querySelectorAll('.ili-assistant__nav-btn');
        buttons.forEach(function (btn) {
            var key = btn.getAttribute('data-category');
            var countEl = btn.querySelector('.ili-assistant__nav-count');
            var n = counts[key] || 0;
            countEl.textContent = n > 0 ? String(n) : '';
            if (n > 0) {
                btn.classList.add('ili-assistant__nav-btn--has-count');
            }
        });
    }

    function setCategory(key) {
        state.selectedCategory = key;
        var buttons = navList.querySelectorAll('.ili-assistant__nav-btn');
        buttons.forEach(function (btn) {
            var active = btn.getAttribute('data-category') === key;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-current', active ? 'true' : 'false');
        });
        // Close mobile drawer after selection.
        panel.classList.remove('ili-assistant--drawer-open');
        if (hooks.onCategorySelect) {
            hooks.onCategorySelect(key);
        }
    }

    // ------------------------------------------------------------------
    // Message rendering
    // ------------------------------------------------------------------
    function renderUserMessage(question) {
        var row = el('div', 'ili-answer__row ili-answer__row--user');
        var bubble = el('div', 'ili-answer__user', question);
        row.appendChild(bubble);
        messages.appendChild(row);
        messages.classList.add('ili-assistant__messages--has-content');
        scrollToBottom();
    }

    function renderAssistantMessage(answer) {
        var row = el('div', 'ili-answer__row ili-answer__row--assistant');
        var bubble = el('div', 'ili-answer__assistant');
        renderer.renderAnswer(bubble, answer);
        row.appendChild(bubble);
        messages.appendChild(row);
        messages.classList.add('ili-assistant__messages--has-content');
        scrollToBottom();
    }

    function renderProcessing() {
        state.isProcessing = true;
        var indicator = el('div', 'ili-assistant__typing', 'Looking through IONLOOP content\u2026');
        indicator.setAttribute('role', 'status');
        messages.appendChild(indicator);
        scrollToBottom();
        return indicator;
    }

    function removeProcessing(indicator) {
        state.isProcessing = false;
        if (indicator && indicator.parentNode) {
            indicator.parentNode.removeChild(indicator);
        }
    }

    function scrollToBottom() {
        requestAnimationFrame(function () {
            messages.scrollTop = messages.scrollHeight;
        });
    }

    // ------------------------------------------------------------------
    // Command palette (autocomplete)
    // ------------------------------------------------------------------
    var cmdState = { visible: false, items: [], selectedIndex: 0 };

    function renderCommandMenu() {
        while (commandMenu.firstChild) {
            commandMenu.removeChild(commandMenu.firstChild);
        }
        if (!cmdState.visible || cmdState.items.length === 0) {
            return;
        }
        cmdState.items.forEach(function (item, index) {
            var row = el('button', 'ili-assistant__command-item' + (index === cmdState.selectedIndex ? ' is-selected' : ''), '');
            row.type = 'button';
            row.setAttribute('role', 'option');
            row.setAttribute('data-command', item.name);
            var nameEl = el('span', 'ili-assistant__command-name', '/' + item.name);
            var descEl = el('span', 'ili-assistant__command-desc', item.description);
            row.appendChild(nameEl);
            row.appendChild(descEl);
            row.addEventListener('click', function () {
                selectCommand(index);
            });
            commandMenu.appendChild(row);
        });
    }

    function showCommandMenu(items) {
        cmdState.items = items;
        cmdState.selectedIndex = 0;
        cmdState.visible = true;
        commandMenu.setAttribute('aria-hidden', 'false');
        renderCommandMenu();
    }

    function hideCommandMenu() {
        cmdState.visible = false;
        commandMenu.setAttribute('aria-hidden', 'true');
        while (commandMenu.firstChild) {
            commandMenu.removeChild(commandMenu.firstChild);
        }
    }

    function moveSelection(delta) {
        if (!cmdState.visible || cmdState.items.length === 0) { return; }
        cmdState.selectedIndex = (cmdState.selectedIndex + delta + cmdState.items.length) % cmdState.items.length;
        renderCommandMenu();
    }

    function selectCommand(index) {
        if (!cmdState.items[index]) { return; }
        executeCommandInput('/' + cmdState.items[index].name);
    }

    function updateCommandMenu() {
        var value = textarea.value;
        if (value.charAt(0) !== '/') {
            hideCommandMenu();
            return;
        }
        var query = value.slice(1).trim();
        var items = commandRouter.getCommands(query);
        if (items.length === 0) {
            hideCommandMenu();
            return;
        }
        showCommandMenu(items);
    }

    // ------------------------------------------------------------------
    // Command execution
    // ------------------------------------------------------------------
    function executeCommandInput(input) {
        hideCommandMenu();
        textarea.value = '';
        textarea.style.height = 'auto';
        updateSendState();

        if (!hooks.onCommand) {
            renderAssistantMessage({
                status: 'answered', type: 'system', intro: 'Commands are unavailable right now.',
                paragraphs: [], bullets: [], actions: [], sources: [], followUps: [], confidence: 0
            });
            return;
        }

        var onCommandDone = function (result) {
            if (!result) { return; }
            if (result.action === 'clear' || result.action === 'reset') {
                clearConversationView();
                if (result.action === 'reset' && state.selectedCategory !== null) {
                    setCategory(null);
                }
            }
            if (result.message) {
                renderAssistantMessage(result.message);
            }
        };

        try {
            var result = hooks.onCommand(input);
            if (result && typeof result.then === 'function') {
                result.then(onCommandDone).catch(function (err) {
                    renderAssistantMessage({
                        status: 'answered', type: 'system', intro: 'Command failed.',
                        paragraphs: [], bullets: [], actions: [], sources: [], followUps: [], confidence: 0
                    });
                });
            } else {
                onCommandDone(result);
            }
        } catch (err) {
            renderAssistantMessage({
                status: 'answered', type: 'system', intro: 'Command failed.',
                paragraphs: [], bullets: [], actions: [], sources: [], followUps: [], confidence: 0
            });
        }
    }

    // ------------------------------------------------------------------
    // Question flow
    // ------------------------------------------------------------------
    function submitQuestion(question) {
        question = String(question || '').trim();
        if (!question || state.isProcessing) {
            return;
        }
        // Slash commands are routed locally, bypassing knowledge search.
        if (question.charAt(0) === '/') {
            executeCommandInput(question);
            return;
        }
        // Remove the welcome state once a real conversation starts.
        if (welcome.parentNode === messages) {
            messages.removeChild(welcome);
        }
        renderUserMessage(question);
        textarea.value = '';
        textarea.setAttribute('rows', '1');
        sendBtn.disabled = true;
        updateSendState();
        var indicator = renderProcessing();

        var onDone = function (result) {
            removeProcessing(indicator);
            var answer = (result && result.response) || result;
            if (answer && answer.type === 'reset') {
                renderUserMessage(question);
            }
            renderAssistantMessage(answer);
            if (hooks.onSubmit && hooks.onSubmitDone) {
                hooks.onSubmitDone(answer);
            }
        };

        if (hooks.onSubmit) {
            try {
                var result = hooks.onSubmit(question);
                if (result && typeof result.then === 'function') {
                    result.then(onDone).catch(function (err) {
                        removeProcessing(indicator);
                        renderAssistantMessage({
                            status: 'insufficient',
                            type: 'insufficient',
                            intro: 'IONLOOP knowledge is temporarily unavailable.',
                            paragraphs: [],
                            bullets: [],
                            actions: [],
                            sources: [],
                            followUps: [],
                            confidence: 0
                        });
                    });
                } else {
                    onDone(result);
                }
            } catch (err) {
                removeProcessing(indicator);
                renderAssistantMessage({
                    status: 'insufficient',
                    type: 'insufficient',
                    intro: 'Something went wrong while answering that question.',
                    paragraphs: [],
                    bullets: [],
                    actions: [],
                    sources: [],
                    followUps: [],
                    confidence: 0
                });
            }
        } else {
            removeProcessing(indicator);
        }
    }

    // ------------------------------------------------------------------
    // Conversation state view
    // ------------------------------------------------------------------
    function clearConversationView() {
        while (messages.firstChild) {
            messages.removeChild(messages.firstChild);
        }
        messages.classList.remove('ili-assistant__messages--has-content');
        messages.appendChild(welcome);
        scrollToBottom();
    }

    function resetView() {
        clearConversationView();
        if (state.selectedCategory !== null) {
            setCategory(null);
        }
    }

    /**
     * Render a compact category-selection response in the conversation.
     * Used by both the Knowledge sidebar and /topics buttons (shared handler).
     */
    function showCategoryOverview(message) {
        if (!message) { return; }
        if (welcome.parentNode === messages) {
            messages.removeChild(welcome);
        }
        renderAssistantMessage(message);
        messages.classList.add('ili-assistant__messages--has-content');
    }

    function updateSendState() {
        var hasText = textarea.value.trim().length > 0;
        sendBtn.disabled = !hasText || state.isProcessing;
        textarea.setAttribute('aria-disabled', state.isProcessing ? 'true' : 'false');
    }

    // ------------------------------------------------------------------
    // Open / close / minimize
    // ------------------------------------------------------------------
    function open() {
        if (state.isMinimized) {
            state.isMinimized = false;
            panel.classList.remove('ili-assistant--minimized');
        }
        state.isOpen = true;
        panel.setAttribute('aria-hidden', 'false');
        document.body.classList.add('ili-assistant-open');
        setTimeout(function () {
            textarea.focus();
        }, 50);
    }

    function close() {
        state.isOpen = false;
        panel.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('ili-assistant-open');
        trigger.focus();
    }

    function minimize() {
        state.isMinimized = true;
        panel.classList.add('ili-assistant--minimized');
        trigger.focus();
    }

    function restore() {
        state.isMinimized = false;
        panel.classList.remove('ili-assistant--minimized');
    }

    // ------------------------------------------------------------------
    // Events
    // ------------------------------------------------------------------
    trigger.addEventListener('click', function () {
        if (state.isMinimized) {
            restore();
            open();
        } else if (state.isOpen) {
            close();
        } else {
            open();
        }
    });

    closeBtn.addEventListener('click', close);
    minimizeBtn.addEventListener('click', function () {
        state.isMinimized ? restore() : minimize();
    });

    drawerToggle.addEventListener('click', function () {
        panel.classList.toggle('ili-assistant--drawer-open');
    });

    textarea.addEventListener('input', function () {
        updateSendState();
        updateCommandMenu();
        // Auto-grow textarea.
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 140) + 'px';
    });

    textarea.addEventListener('keydown', function (event) {
        if (cmdState.visible) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveSelection(1);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveSelection(-1);
                return;
            }
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                selectCommand(cmdState.selectedIndex);
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                hideCommandMenu();
                return;
            }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submitQuestion(textarea.value);
        }
    });

    sendBtn.addEventListener('click', function () {
        submitQuestion(textarea.value);
    });

    navList.addEventListener('click', function (event) {
        var btn = event.target.closest ? event.target.closest('.ili-assistant__nav-btn') : null;
        if (btn) {
            var key = btn.getAttribute('data-category');
            if (state.selectedCategory === key) {
                setCategory(null);
            } else {
                setCategory(key);
            }
        }
    });

    welcome.addEventListener('click', function (event) {
        var btn = event.target.closest ? event.target.closest('.ili-assistant__suggested-btn') : null;
        if (btn) {
            submitQuestion(btn.textContent);
        }
    });

    // Suggested follow-ups and /topics category buttons rendered inside messages.
    messages.addEventListener('click', function (event) {
        var topicBtn = event.target.closest ? event.target.closest('[data-ili-category]') : null;
        if (topicBtn) {
            setCategory(topicBtn.getAttribute('data-ili-category'));
            return;
        }
        var btn = event.target.closest ? event.target.closest('[data-ili-suggestion],[data-ili-followup]') : null;
        if (btn) {
            submitQuestion(btn.textContent);
        }
    });

    // Escape closes the panel.
    document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && state.isOpen) {
            close();
        }
    });

    // ------------------------------------------------------------------
    // Mount
    // ------------------------------------------------------------------
    var root = el('div', 'ili-assistant');
    root.appendChild(trigger);
    root.appendChild(panel);
    document.body.appendChild(root);

    // Keep panel above page dimmer/modal stack but below Bootstrap modals.
    // (z-index handled in SCSS.)

    var controller = {
        open: open,
        close: close,
        minimize: minimize,
        restore: restore,
        isOpen: function () { return state.isOpen; },
        updateCategoryCounts: updateCategoryCounts,
        setCategory: setCategory,
        submit: submitQuestion,
        clearConversation: clearConversationView,
        reset: resetView,
        getPanel: function () { return panel; },
        showCategoryOverview: showCategoryOverview
    };

    // Populate counts if available.
    if (hooks.getCategoryCounts) {
        Promise.resolve(hooks.getCategoryCounts()).then(updateCategoryCounts).catch(function () {});
    }

    return controller;
}

module.exports = {
    buildAssistantUI: buildAssistantUI,
    CATEGORIES: CATEGORIES,
    SUGGESTED_QUESTIONS: SUGGESTED_QUESTIONS
};
